import type { VariantProps } from 'class-variance-authority'
import type { HTMLAttributes } from 'react'
import type { ICalendarEvent } from '@/features/calendar/interfaces'

import { cva } from 'class-variance-authority'

import { differenceInMinutes, format, parseISO } from 'date-fns'

import { EventDetailsDialog } from '@/features/calendar/components/dialogs/event-details-dialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { useCalendar } from '../../contexts/use-calendar'

const calendarWeekBookingCardVariants = cva(
  'flex select-none flex-col gap-0.5 truncate whitespace-nowrap rounded-md border px-2 py-1.5 text-xs focus-visible:outline-offset-2',
  {
    variants: {
      color: {
        // Colored variants
        'blue': 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300',
        'green': 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300',
        'red': 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300',
        'yellow': 'border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-300',
        'purple': 'border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-800 dark:bg-purple-950 dark:text-purple-300',
        'orange': 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300',

        // Dot variants
        'blue-dot': 'border-b-primary bg-bg-secondary text-t-primary [&_svg]:fill-blue-600',
        'green-dot': 'border-b-primary bg-bg-secondary text-t-primary [&_svg]:fill-green-600',
        'red-dot': 'border-b-primary bg-bg-secondary text-t-primary [&_svg]:fill-red-600',
        'orange-dot': 'border-b-primary bg-bg-secondary text-t-primary [&_svg]:fill-orange-600',
        'purple-dot': 'border-b-primary bg-bg-secondary text-t-primary [&_svg]:fill-purple-600',
        'yellow-dot': 'border-b-primary bg-bg-secondary text-t-primary [&_svg]:fill-yellow-600',
      },
    },
    defaultVariants: {
      color: 'blue-dot',
    },
  },
)

interface IProps extends HTMLAttributes<HTMLDivElement>, Omit<VariantProps<typeof calendarWeekBookingCardVariants>, 'color'> {
  training: ICalendarEvent
}

export function EventBlock({ className, training }: IProps) {
  const { badgeVariant } = useCalendar()

  const start = parseISO(training.startAt)
  const end = parseISO(training.endAt)

  const durationInMinutes = differenceInMinutes(end, start)
  const heightInPixels = (durationInMinutes / 60) * 96 - 8

  const color = (badgeVariant === 'dot' ? `${training.color}-dot` : training.color) as VariantProps<typeof calendarWeekBookingCardVariants>['color']

  const calendarWeekBookingCardClasses = cn(calendarWeekBookingCardVariants({ color, className }), durationInMinutes < 35 && 'py-0 justify-center')

  const tooltipContent = (
    <div className="max-w-xs space-y-2">
      <div className="font-semibold">{training.title}</div>
      <div className="text-xs opacity-90">
        <div>📅 {format(start, 'MMM dd, yyyy')}</div>
        <div>🕐 {format(start, 'HH:mm')} - {format(end, 'HH:mm')}</div>
        {training.location && <div>📍 {training.location}</div>}
        {training.category && <div>🏷️ {training.category}</div>}
        {typeof training.registrations === 'number' && (
          <div>👥 {training.registrations} registered</div>
        )}
        {typeof training.availableSpots === 'number' && training.availableSpots > 0 && (
          <div>🪑 {training.availableSpots} spots remaining</div>
        )}
      </div>
      {training.description && (
        <div className="text-xs opacity-75 line-clamp-3">{training.description}</div>
      )}
    </div>
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <EventDetailsDialog
          training={training}
        >
          <div
            role="button"
            tabIndex={0}
            className={cn(calendarWeekBookingCardClasses, `bg-${training.color}-100`)}
            style={{ height: `${heightInPixels}px` }}
          >
            <p className="whitespace-pre-wrap font-semibold">{training.title}</p>
            {durationInMinutes > 25 && (
              <p className="mt-1.5 text-[10px] opacity-90">
                {format(start, 'HH:mm a')}
                {' '}
                -
                {format(end, 'HH:mm a')}
              </p>
            )}
          </div>
        </EventDetailsDialog>
      </TooltipTrigger>
      <TooltipContent side="top" className="bg-black text-white">
        {tooltipContent}
      </TooltipContent>
    </Tooltip>
  )
}
